import clsx from "clsx";
import React, { useEffect } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { complexCommands, PackageTypes } from "src/data/commands";
import { useSelector } from "src/store";

import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { Device, Group } from "@dashboard/devices/types";
import { JSONEditor } from "@json-editor/json-editor/dist/jsoneditor.js";
import { Box, Typography, useTheme } from "@mui/material";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { showErrorToast } from "@shared/toasts/Toasts";

import { filterCommands } from "../helpers/filterCommands";
import { JsonEditorCommandType, updateEditor, validateEditor } from "../helpers/jsonEditor";
import { usePolicyCommands } from "../hooks";
import { FormValues } from "../NewCommandDialog";
import { InputItemContainer, useStyles } from "../styles";

let complexEditor: JSONEditor | null = null;

type Props = {
    device?: Device;
    group?: Group;
    submitRef: React.RefObject<HTMLButtonElement | null>;
    editorRef: React.RefObject<HTMLDivElement | null>;
    isPolicy?: boolean;
    toggleDialog: () => void;
    createCommand?: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
};

export const ComplexCommands = ({ device, group, submitRef, editorRef, isPolicy, toggleDialog, createCommand }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);
    const theme = useTheme();

    const policyCommands = group?.policy || [];
    const devices = useSelector((state) => selectDevicesByGroupId(state, group?.id));

    const { updatePolicyCommands } = usePolicyCommands();

    useEffect(() => {
        let availablePackages: string[] = [];
        let userPackages: string[] = [];
        let systemPackages: string[] = [];
        let launchablePackages: string[] = [];

        if (device && device.lastState?.packages) {
            const { packages } = device.lastState;
            availablePackages = packages.map((p) => p.package);

            userPackages = packages.filter((p) => !p.system).map((p) => p.package);
            systemPackages = packages.filter((p) => p.system).map((p) => p.package);
            launchablePackages = packages?.filter((p) => p.launchable).map((p) => p.package);
        } else {
            // for group commands get all packages from all devices in a group
            const allPackages = [...new Set(devices.flatMap((d) => d?.lastState?.packages || []))].filter((item) => item).sort();

            if (allPackages.length) {
                availablePackages = allPackages.map((p) => p.package);
                userPackages = allPackages.filter((p) => !p.system).map((p) => p.package);
                systemPackages = allPackages.filter((p) => p.system).map((p) => p.package);
                launchablePackages = allPackages.filter((p) => p.launchable).map((p) => p.package);
            }
        }

        // inject packages into jsonObject
        complexCommands.forEach((command) => {
            if (command.schema.properties?.package?.packageType === undefined) {
                return;
            }

            switch (command.schema.properties.package?.packageType) {
                case PackageTypes.AVAILABLE:
                    command.schema.properties.package.enum = availablePackages.sort() as [];
                    command.schema.properties.package.default = availablePackages[0];
                    break;
                case PackageTypes.USER:
                    command.schema.properties.package.enum = userPackages.sort() as [];
                    command.schema.properties.package.default = userPackages[0];
                    break;
                case PackageTypes.SYSTEM:
                    command.schema.properties.package.enum = systemPackages.sort() as [];
                    command.schema.properties.package.default = systemPackages[0];
                    break;
                case PackageTypes.LAUNCHABLE:
                    command.schema.properties.package.enum = launchablePackages.sort() as [];
                    command.schema.properties.package.default = launchablePackages[0];
                    break;
            }
        });
        const filteredCommands = filterCommands(complexCommands, isPolicy);

        complexEditor = updateEditor(
            complexEditor,
            editorRef,
            JsonEditorCommandType.ComplexCommands,
            filteredCommands,
            filteredCommands[0].name,
        );

        // destroy the editor to prevent duplicates when component is unmounting
        return () => {
            if (complexEditor) {
                complexEditor.destroy();
            }
        };
    }, [device, device?.lastState, devices, editorRef, isPolicy]);

    const onSubmit = async (values: FormValues) => {
        const errors = validateEditor(complexEditor, editorRef.current);
        if (errors) {
            return;
        }

        const input = complexEditor && complexEditor.getValue();

        const availableCommands = filterCommands(complexCommands, isPolicy);
        const command = availableCommands.find((obj) => obj.name === values.commandType);
        if (!command) {
            showErrorToast(t("modals:newPolicyCommand.toast.commandTemplateNotFound"));
            return;
        }

        const payload = { command: values.commandType, caption: command.schema.title, ...input };
        if (isPolicy && group) {
            const policyPayload = Array.isArray(policyCommands) && [...policyCommands, payload];
            await updatePolicyCommands({ groupId: group.id, policy: JSON.stringify(policyPayload) });
        } else if (createCommand) {
            await createCommand(payload, null);
        }
        toggleDialog();
    };

    const availableCommands = filterCommands(complexCommands, isPolicy);

    return (
        <React.Fragment>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={{ commandType: availableCommands[0].name }}
                render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit} id="editCommand">
                        <div className={classes.sectionContainer}>
                            <InputItemContainer>
                                <Box className={clsx([classes.input, classes.select])}>
                                    <div className="form-group">
                                        <Typography style={{ marginBottom: theme.spacing(1) }} variant="subtitle1">
                                            <b>{t("modals:newPolicyCommand.commandType")}</b>
                                        </Typography>
                                        <ControlledSelectField
                                            name="commandType"
                                            className="form-control"
                                            searchable
                                            options={availableCommands.map((obj) => ({
                                                value: obj.name,
                                                label: obj.schema.title,
                                            }))}
                                            onChange={() => {
                                                const filteredCommands = filterCommands(complexCommands, isPolicy);
                                                complexEditor = updateEditor(
                                                    complexEditor,
                                                    editorRef,
                                                    JsonEditorCommandType.ComplexCommands,
                                                    filteredCommands,
                                                    form.getFieldState("commandType")?.value ?? filteredCommands[0].name,
                                                );
                                            }}
                                            fullWidth
                                        />
                                    </div>
                                </Box>
                            </InputItemContainer>
                            <InputItemContainer style={{ paddingBottom: theme.spacing(4) }}>
                                <Typography className={classes.commandParametersLabel} variant="subtitle1">
                                    <b>{t("modals:newPolicyCommand.commandParameters")}</b>
                                </Typography>
                                <div id="json-editor" ref={editorRef}>
                                    {/* filled out by jsoneditor */}
                                </div>
                            </InputItemContainer>
                            <button ref={submitRef} type="submit" hidden />
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};
